import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { domains, categories, articles } from '@/mocks/data';

export default function HomePage() {
  const { t } = useTranslation();
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const featuredDomains = domains.filter((d) => d.featured && d.status === 'online').slice(0, 6);
  const onlineCount = domains.filter((d) => d.status === 'online').length;
  const latestArticles = articles.slice(0, 3);

  const faqs = [
    { q: '日韩在线永久域名是什么？', a: '日韩在线永久域名是一个专门收录日韩在线内容访问域名的平台，我们持续监控和更新域名状态，确保您收藏的域名始终可用，再也不用担心网址失效找不到内容。' },
    { q: '为什么域名会经常失效？', a: '日韩在线内容域名失效通常是因为DNS污染、域名被屏蔽或服务器迁移等原因。本站通过持续监控和及时更新，为您提供最新可用的域名地址。' },
    { q: '本站收录的域名是否安全？', a: '是的，本站所有收录的域名均经过人工审核，我们只收录正规、安全的日韩在线内容站点，绝不收录含有恶意软件或欺诈内容的网站。' },
    { q: '如何确保能够稳定访问？', a: '建议您将本站加入浏览器书签，同时收藏多个备用域名。本站每日更新域名状态，您可以随时回来查看最新的可用域名列表。' },
    { q: '域名更新频率是怎样的？', a: '我们的团队7×24小时监控域名状态，每天至少进行一次全面检查和更新。当发现域名失效时，我们会在2小时内完成更新替换。' },
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '日韩在线永久域名',
    url: 'https://zhihaiyunxin.cn',
    description: t('seo.homeDesc'),
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://zhihaiyunxin.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20elegant%20Japanese%20and%20Korean%20cultural%20elements%20abstract%20background%20with%20soft%20cherry%20blossom%20petals%20and%20subtle%20geometric%20patterns%2C%20warm%20amber%20and%20soft%20coral%20tones%2C%20minimalist%20artistic%20composition%2C%20clean%20gradient%20with%20gentle%20texture%2C%20professional%20editorial%20style%2C%20harmonious%20atmosphere%2C%20no%20text%2C%20no%20people%2C%20abstract%20design&width=1600&height=900&seq=hero-bg-2026-01&orientation=landscape"
              alt="日韩在线永久域名背景"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          </div>

          <div className="relative z-10 w-full text-center px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 md:mb-6 font-heading leading-tight">
                日韩在线永久域名
              </h1>
              <p className="text-lg md:text-xl text-background-50/80 mb-2 font-heading">
                稳定不丢失的访问入口
              </p>
              <p className="text-sm md:text-base text-background-50/60 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
                为您提供最新、最稳定的日韩在线域名收录，告别频繁换址烦恼，永久书签收藏，随时畅享日韩正版内容。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/video"
                  className="px-8 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  立即探索
                </Link>
                <a
                  href="#featured"
                  className="px-8 py-3 bg-background-50/15 text-background-50 border border-background-50/30 rounded-lg text-sm font-medium hover:bg-background-50/25 transition-colors whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </a>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background-50 to-transparent"></div>
        </section>

        {/* Stats Bar */}
        <section className="w-full px-4 md:px-6 -mt-6 relative z-20">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
              {[
                { label: '收录域名', value: `${domains.length}+`, icon: 'ri-global-line' },
                { label: '内容分类', value: `${categories.length}`, icon: 'ri-folders-line' },
                { label: '服务用户', value: '50万+', icon: 'ri-user-heart-line' },
                { label: '每日更新', value: '365天', icon: 'ri-refresh-line' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-xl p-4 md:p-5 text-center border border-background-200/60"
                >
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">{stat.value}</div>
                  <div className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Domains */}
        <section id="featured" className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                推荐域名
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                精选最稳定、最受欢迎的日韩在线域名
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {featuredDomains.map((domain) => (
                <Link
                  key={domain.id}
                  to={`/article/${domain.id}`}
                  className="group bg-background-50 rounded-xl p-5 border border-background-200/60 hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                      <i className="ri-link text-accent-600 text-lg"></i>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mr-1.5"></span>
                      在线
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors font-heading">
                    {domain.name}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                    {domain.description}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {domain.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-background-100 rounded text-xs text-foreground-500"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-background-100 flex items-center justify-between">
                    <span className="text-xs text-foreground-400">
                      更新于 {domain.updateTime}
                    </span>
                    <span className="text-xs text-foreground-400">
                      <i className="ri-eye-line mr-1"></i>{(domain.visits / 10000).toFixed(1)}万
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/category/video"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部域名
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                内容分类
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                按内容类型浏览日韩在线域名
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 rounded-xl p-5 text-center border border-background-200/60 hover:border-secondary-300 transition-all duration-300 no-underline cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-secondary-100 group-hover:bg-secondary-200 transition-colors">
                    <i className={`${cat.icon} text-secondary-600 text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1 whitespace-nowrap font-heading">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-400">
                    {cat.count} 个域名
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                最新文章
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                日韩域名使用指南与最新资讯
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 no-underline cursor-pointer"
                >
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                        {categories.find((c) => c.id === article.category)?.name || article.category}
                      </span>
                      <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-xs text-foreground-400">
                        <i className="ri-eye-line mr-1"></i>{(article.visits / 10000).toFixed(1)}万
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                常见问题
              </h2>
              <p className="text-sm md:text-base text-foreground-500">
                关于日韩在线永久域名的常见疑问解答
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden"
                >
                  <button
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950 pr-4">
                      {faq.q}
                    </h3>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${
                      activeFaq === index ? 'bg-primary-100 text-primary-600' : 'bg-background-100 text-foreground-400'
                    }`}>
                      <i className={`text-sm transition-transform duration-300 ${activeFaq === index ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      activeFaq === index ? 'max-h-60 pb-5 px-5' : 'max-h-0'
                    }`}
                  >
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full px-4 md:px-6 py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20gradient%20background%20with%20soft%20Japanese%20aesthetic%20elements%2C%20delicate%20geometric%20line%20patterns%2C%20warm%20amber%20and%20coral%20tones%2C%20minimalist%20composition%2C%20clean%20and%20professional%2C%20abstract%20artistic%20texture%2C%20no%20text%2C%20no%20people%2C%20subtle%20elegance&width=1600&height=600&seq=cta-bg-2026-01&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/60"></div>
          </div>

          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-4 font-heading">
              立即收藏，永不丢失
            </h2>
            <p className="text-sm md:text-base text-background-50/70 mb-8 leading-relaxed">
              将本站加入书签，您就拥有了一个永不过期的日韩在线内容访问入口。域名每日更新维护，确保您的访问从不中断。
            </p>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(window.location.href).then(() => {
                    alert('已复制本站地址，请粘贴到浏览器书签中！');
                  });
                }
              }}
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-bookmark-line"></i>
              收藏本站
            </a>
          </div>
        </section>
      </main>
    </>
  );
}